import React from 'react';
import { Routes, Route, BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import BusinessPages from './pages/BusinessPages';
import PageDetail from './pages/PageDetail';
import TestimonyWall from './pages/TestimonyWall';
import Pulse from './pages/Pulse';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import Messaging from './pages/Messaging';
import EnhancedAdminDashboard from './pages/EnhancedAdminDashboard';
import ArtistVerification from './pages/ArtistVerification';
import CreateAd from './pages/CreateAd';
import AdvertiserDashboard from './pages/AdvertiserDashboard';
import EditAd from './pages/EditAd';
import EarningsDashboard from './pages/EarningsDashboard';
import WalletTransactions from './pages/WalletTransactions';
import ConversionsDashboard from './pages/ConversionsDashboard';
import Landing from './pages/Landing';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectIfAuthed from './components/RedirectIfAuthed';
import NotFound from './pages/NotFound';
import AdsManager from './pages/AdsManager';
import HashtagPage from './pages/HashtagPage';
import Saved from './pages/Saved';
import FollowersFollowing from './pages/FollowersFollowing';
import PostPage from './pages/Post';
import UserByUsername from './pages/UserByUsername';
import Stories from './pages/Stories';
import Search from './pages/Search';
import Notifications from './pages/Notifications';
import About from './pages/About';
import Help from './pages/Help';
import Guides from './pages/Guides';
import CreatorStudio from './pages/CreatorStudio';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AdminCustomizer from './pages/AdminCustomizer';
import AdminSEO from './pages/AdminSEO';
import './App.css';
import MobileBottomNav from './components/MobileBottomNav';
import CallManager from './components/CallManager';
// Lazy-loaded large routes last (after static imports to satisfy ESLint import/first)
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
const Live = React.lazy(() => import('./pages/Live'));
const GoLive = React.lazy(() => import('./pages/GoLive'));
const LiveStreamViewer = React.lazy(() => import('./pages/LiveStreamViewer'));
const MusicLibraryManager = React.lazy(() => import('./pages/MusicLibraryManager'));
const AppealsAdmin = React.lazy(() => import('./pages/AppealsAdmin'));
const GroupMembersPage = React.lazy(() => import('./pages/GroupMembersPage'));

function AppShell() {
  const location = useLocation();
  const path = location.pathname;
  const hideBottomNavRoutes = ['/', '/login', '/signup'];
  const shouldShowBottomNav = !hideBottomNavRoutes.includes(path);

  return (
    <div className="app-shell route-fade" key={path}>
      <Routes>
  <Route path="/" element={<RedirectIfAuthed><Landing /></RedirectIfAuthed>} />
  <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
  <Route path="/signup" element={<RedirectIfAuthed><Signup /></RedirectIfAuthed>} />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        {/* Username-based profile route (canonical) */}
        <Route
          path="/:username"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
          <Route
            path="/pages"
            element={
              <ProtectedRoute>
                <BusinessPages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/page/:pageId"
            element={
              <ProtectedRoute>
                <PageDetail />
              </ProtectedRoute>
            }
          />
          {/* Earnings route (new integrated dashboard) */}
          <Route
            path="/earnings"
            element={
              <ProtectedRoute>
                <EarningsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/music-library"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<div style={{padding:20}}>Loading music library…</div>}>
                  <MusicLibraryManager />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
        <Route
          path="/testimony"
          element={
            <ProtectedRoute>
              <TestimonyWall />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pulse"
          element={
            <ProtectedRoute>
              <Pulse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups"
          element={
            <ProtectedRoute>
              <Groups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:groupId"
          element={
            <ProtectedRoute>
              <GroupDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:groupId/members"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<div style={{padding:20}}>Loading members…</div>}>
                <GroupMembersPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messaging />
            </ProtectedRoute>
          }
        />
        {/* Branded admin route (avoid /admin). Alias old path to new */}
        <Route
          path="/studio"
          element={
            <ProtectedRoute>
              <EnhancedAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute roles={['admin','moderator','super_admin']}>
              <React.Suspense fallback={<div style={{padding:20}}>Loading analytics…</div>}>
                <AnalyticsDashboard />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/studio/customizer"
          element={
            <ProtectedRoute roles={['admin','moderator','super_admin']}>
              <AdminCustomizer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/studio/seo"
          element={
            <ProtectedRoute roles={['admin','moderator','super_admin']}>
              <AdminSEO />
            </ProtectedRoute>
          }
        />
        <Route
          path="/appeals"
          element={
            <ProtectedRoute roles={['admin','moderator','super_admin']}>
              <React.Suspense fallback={<div style={{padding:20}}>Loading appeals…</div>}>
                <AppealsAdmin />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="/admin-dashboard" element={<Navigate to="/studio" replace />} />
        <Route
          path="/artist-verification"
          element={
            <ProtectedRoute>
              <ArtistVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads"
          element={
            <ProtectedRoute>
              <AdsManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-ad"
          element={
            <ProtectedRoute>
              <CreateAd />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advertiser-dashboard"
          element={
            <ProtectedRoute>
              <AdvertiserDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-ad/:adId"
          element={
            <ProtectedRoute>
              <EditAd />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hashtag/:tag"
          element={
            <ProtectedRoute>
              <HashtagPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/saved"
          element={
            <ProtectedRoute>
              <Saved />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connections"
          element={
            <ProtectedRoute>
              <FollowersFollowing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connections/:userId"
          element={
            <ProtectedRoute>
              <FollowersFollowing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/post/:postId"
          element={
            <ProtectedRoute>
              <PostPage />
            </ProtectedRoute>
          }
        />
        {/* Legacy alias retained for backward compatibility */}
        <Route
          path="/u/:username"
          element={
            <ProtectedRoute>
              <UserByUsername />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<div style={{padding:20}}>Loading live…</div>}>
                <Live />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/live/:streamId"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<div style={{padding:20}}>Loading stream…</div>}>
                <LiveStreamViewer />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/live/rewatch/:streamId"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<div style={{padding:20}}>Loading recording…</div>}>
                <LiveStreamViewer />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/go-live"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<div style={{padding:20}}>Loading studio…</div>}>
                <GoLive />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories"
          element={
            <ProtectedRoute>
              <Stories />
            </ProtectedRoute>
          }
        />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
  <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
  <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
  <Route path="/docs" element={<ProtectedRoute><Guides variant="docs" /></ProtectedRoute>} />
  <Route path="/guides" element={<ProtectedRoute><Guides variant="docs" /></ProtectedRoute>} />
  <Route path="/playbook" element={<ProtectedRoute><Guides variant="playbook" /></ProtectedRoute>} />
  <Route path="/creator-studio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />
  <Route path="/privacy" element={<ProtectedRoute><Privacy /></ProtectedRoute>} />
  <Route path="/terms" element={<ProtectedRoute><Terms /></ProtectedRoute>} />
  <Route path="/wallet-transactions" element={<ProtectedRoute><WalletTransactions /></ProtectedRoute>} />
  <Route path="/conversions" element={<ProtectedRoute><ConversionsDashboard /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {shouldShowBottomNav && <MobileBottomNav />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppShell />
      <CallManager />
    </BrowserRouter>
  );
}

export default App;

